<script lang="ts" setup>
import { computed } from 'vue'
import type { IProps } from './RecommendationTable.types'
import type { IUiTableColumn } from '@/components/UiTable/UiTable.types'
import type { IAdaptRecommendation } from '@/types/investor'
import { formatDate } from '@/utils/date'

defineProps<IProps>()
const model = defineModel<number>()

const { t } = useI18n()

const recommendationIcons = {
  Kupuj: 'icon-kupuj.png',
  Trzymaj: 'icon-trzymaj.png',
  Sprzedaj: 'icon-sprzedaj.png',
} as const

const columns = computed<IUiTableColumn<IAdaptRecommendation>[]>(() => [
  {
    key: 'date',
    title: t('investor.stock.recommendation.table.heading1'),
    value: (item) => formatDate(item.date),
  },
  {
    key: 'recommendation',
    title: t('investor.stock.recommendation.table.heading2'),
  },
  {
    key: 'targetPrice',
    title:  t('investor.stock.recommendation.table.heading3'),
    value: (item) =>   new Intl.NumberFormat('pl-PL', {
      useGrouping: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(item.targetPrice),
  },
  {
    key: 'issuePrice',
    title: t('investor.stock.recommendation.table.heading4'),
    value: (item) =>   new Intl.NumberFormat('pl-PL', {
      useGrouping: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(item.issuePrice),
  },
  {
    key: 'institution',
    title:  t('investor.stock.recommendation.table.heading5'),
  },
])
</script>

<template>
  <div class="investor-stock-recommendation-table">
    <div class="investor-stock-recommendation-table__container">
      <h2 class="investor-stock-recommendation-table__title">
        {{ $t('investor.stock.recommendation.table.mainHeading') }}
      </h2>

      <div class="investor-stock-recommendation-table__table">
        <UiTable
          :columns="columns"
          :items="items"
        >
          <template #cell-recommendation="{ item }">
            <div class="investor-stock-recommendation-table__recommendation_cell">
              <img
                :src="`/icons/investor/recommendation/${recommendationIcons[item.recommendation as keyof typeof recommendationIcons]}`"
                aria-hidden="true"
              >

              {{ $t(`investor.stock.recommendation.table.recommendation.${item.recommendation.toLowerCase()}`) }}
            </div>
          </template>
        </UiTable>
    
        <UiPagination
          v-model="model"
          class="investor-stock-recommendation-table__pagination"
          :page-count="Math.ceil(total / itemsPerPage)"
          :total="total"
          :items-per-page="itemsPerPage"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.investor-stock-recommendation-table {
  
  @include section-investor();

  &__container {
    @include section-investor__container();
  }

  &__title {
    @include h5;
    margin-bottom: 3.2rem;
    color: $text-400;
    text-align: center;
  }

  &__recommendation_cell {
    display: flex;
    align-items: center;
    gap: 0.2rem;
  }
  
  &__pagination {
    justify-content: center;
    margin-top: 3.2rem;
  }

  @include atM {

    &__title {
      margin-bottom: 6.4rem;
    }
    
    &__recommendation_cell {
      gap: 0.4rem;
    }
  }
}
</style>
